// The rules behind releases.ts, as plain functions.
//
// Shared by three callers that must agree: the app (what to show whom), the
// unit tests, and scripts/releases.mjs (what the deploy refuses). The script
// loads this file outside webpack, so the only import allowed is the type-only
// one below — it is erased when transpiled.

import type { Release } from './releases'

type Version = [number, number, number]

const VERSION_PATTERN = /^(0|[1-9]\d*)\.(0|[1-9]\d*)\.(0|[1-9]\d*)$/

export const parseVersion = (version: string | null | undefined) => {
    const match = VERSION_PATTERN.exec(version ?? '')
    return match ? (match.slice(1).map(Number) as Version) : null
}

/** Negative when `a` is older than `b`. Unparseable versions sort oldest. */
export const compareVersions = (a: string, b: string) => {
    const left = parseVersion(a) ?? [-1, -1, -1]
    const right = parseVersion(b) ?? [-1, -1, -1]
    for (let i = 0; i < 3; i++) {
        if (left[i] !== right[i]) return left[i] - right[i]
    }
    return 0
}

export type Bump = 'major' | 'minor' | 'patch'

/** The single legal step from `previous` to `next`, or null if it is not one. */
export const bumpBetween = (previous: string, next: string): Bump | null => {
    const from = parseVersion(previous)
    const to = parseVersion(next)
    if (!from || !to) return null
    const [major, minor, patch] = from
    if (to[0] === major + 1 && to[1] === 0 && to[2] === 0) return 'major'
    if (to[0] === major && to[1] === minor + 1 && to[2] === 0) return 'minor'
    if (to[0] === major && to[1] === minor && to[2] === patch + 1)
        return 'patch'
    return null
}

export const nextVersions = (version: string) => {
    const [major, minor, patch] = parseVersion(version) ?? [0, 0, 0]
    return {
        patch: `${major}.${minor}.${patch + 1}`,
        minor: `${major}.${minor + 1}.0`,
        major: `${major + 1}.0.0`,
    }
}

const MAX_CHANGES = 8
const MAX_TITLE = 70
const MAX_TEXT = 140
const PERSIAN = /[؀-ۿ]/
// What a commit message looks like, and a member should never read.
const DEVELOPER_WORDS =
    /\b(feat|fix|refactor|chore|build|deploy|commit|MR|PWA|API|endpoint|PostHog|bug)\b|\w+\(/i
const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/

const isRealDate = (date: string) => {
    if (!ISO_DATE.test(date)) return false
    const time = Date.parse(`${date}T00:00:00Z`)
    return !Number.isNaN(time) && new Date(time).toISOString().startsWith(date)
}

const textProblem = (label: string, text: string, max: number) => {
    if (!text || !text.trim()) return `${label} is empty`
    if (text.length > max) return `${label} is longer than ${max} characters`
    if (!PERSIAN.test(text)) return `${label} must be written in Persian`
    // Bidi reorders an RTL line, so → between Persian words points backwards.
    if (text.includes('→'))
        return `${label} uses → which renders backwards in Persian — write «از … به …»`
    const jargon = DEVELOPER_WORDS.exec(text)
    if (jargon)
        return `${label} reads like a commit message («${jargon[0]}») — write what the member notices`
    return null
}

/** Everything wrong with the release list; empty when it may ship. */
export const releaseProblems = (releases: Release[]): string[] => {
    if (!Array.isArray(releases) || releases.length === 0)
        return ['RELEASES is empty — there must be at least one release']

    const problems: string[] = []
    releases.forEach((release, index) => {
        const at = `release ${release?.version || `#${index + 1}`}`
        if (!parseVersion(release.version))
            problems.push(`${at}: version must look like 1.2.3`)
        if (!isRealDate(release.date))
            problems.push(`${at}: date must be a real YYYY-MM-DD`)

        const title = textProblem(`${at}: title`, release.title, MAX_TITLE)
        if (title) problems.push(title)

        if (!Array.isArray(release.changes) || release.changes.length === 0) {
            problems.push(`${at}: needs at least one change`)
        } else {
            if (release.changes.length > MAX_CHANGES)
                problems.push(
                    `${at}: more than ${MAX_CHANGES} changes — keep the ones members will notice`
                )
            release.changes.forEach((change, i) => {
                if (!['new', 'better', 'fix'].includes(change.kind))
                    problems.push(
                        `${at}: change ${i + 1} kind must be new, better or fix`
                    )
                const text = textProblem(
                    `${at}: change ${i + 1}`,
                    change.text,
                    MAX_TEXT
                )
                if (text) problems.push(text)
            })
        }

        const older = releases[index + 1]
        if (!older) return
        const bump = bumpBetween(older.version, release.version)
        if (!bump) {
            const next = nextVersions(older.version)
            problems.push(
                `${at}: must be exactly one step after ${older.version} — ${next.patch}, ${next.minor} or ${next.major} (newest goes first)`
            )
        } else if (
            bump === 'patch' &&
            release.changes?.some((change) => change.kind === 'new')
        ) {
            problems.push(
                `${at}: has a "new" change, so it is a minor release — use ${
                    nextVersions(older.version).minor
                }`
            )
        }
        if (isRealDate(release.date) && isRealDate(older.date)) {
            if (release.date < older.date)
                problems.push(`${at}: dated before ${older.version}`)
        }
    })
    return problems
}

/** How many releases the sheet lists after an update, newest first. */
export const MAX_UNSEEN = 3

/**
 * What to tell a member who opens this build.
 *
 * `lastSeen` is the newest version they have already been told about. Without
 * one, this browser predates the feature: they hear about the newest release
 * only if their account is older than it — someone who signed up after a
 * release never had the version before it, so there is nothing "new" for them.
 */
export const unseenReleases = (
    releases: Release[],
    lastSeen: string | null,
    joinedAt: number | null
): Release[] => {
    if (lastSeen && parseVersion(lastSeen)) {
        return releases
            .filter((release) => compareVersions(release.version, lastSeen) > 0)
            .slice(0, MAX_UNSEEN)
    }
    const [latest] = releases
    if (!latest || joinedAt === null || Number.isNaN(joinedAt)) return []
    return joinedAt < Date.parse(`${latest.date}T00:00:00Z`) ? [latest] : []
}

/**
 * Screens the sheet must not cover: signing in, the public landing, a bank
 * gateway returning, identity verification. Everywhere else it waits for the
 * next one of these to be left.
 */
const DO_NOT_INTERRUPT = [
    '/auth',
    '/q',
    '/gateway-callback',
    '/group-all-payment',
    '/pay-to-box',
    '/authentication_with_national_code',
    '/authentication-user',
]

export const mayInterrupt = (pathname: string) =>
    !DO_NOT_INTERRUPT.some(
        (prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`)
    )
